import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '../services/api';

// Get OAuth configuration from environment variables
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || 'YOUR_GOOGLE_CLIENT_ID';
const GOOGLE_CLIENT_SECRET = import.meta.env.VITE_GOOGLE_CLIENT_SECRET || 'YOUR_GOOGLE_CLIENT_SECRET';
// Use the explicit redirect URI from environment variables
const GOOGLE_REDIRECT_URI = import.meta.env.VITE_GOOGLE_REDIRECT_URI || `${window.location.origin}/callback`;

interface GoogleTokenResponse {
  access_token: string;
  expires_in: number;
  token_type: string;
  scope: string;
  id_token: string;
}

interface GoogleUserInfo {
  id: string;
  email: string;
  verified_email: boolean;
  name: string;
  given_name: string;
  family_name: string;
  picture: string;
  locale: string;
}

const CallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState<string>('processing');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Parse the URL query parameters
        const searchParams = new URLSearchParams(location.search);
        const code = searchParams.get('code');
        const state = searchParams.get('state'); // This is the requestId we passed earlier
        const error = searchParams.get('error');
        
        if (error) {
          setError(`Google authentication error: ${error}`);
          setStatus('error');
          return;
        }
        
        if (!code || !state) {
          setError('Missing authentication code or state parameter');
          setStatus('error');
          return;
        }
        
        // Exchange the code for Google tokens
        // const tokenResponse = await exchangeCodeForTokens(code);
        
        // if (!tokenResponse) {
        //   setError('Failed to exchange code for tokens');
        //   setStatus('error');
        //   return;
        // }
        
        // Get user info from Google
        const userInfo = await getUserInfo(code);
        
        if (!userInfo) {
          setError('Failed to fetch user info');
          setStatus('error');
          return;
        }
        
        // Complete the login process by updating the login request status
        await api.completeLogin(state, userInfo.id, userInfo.name, userInfo.email, userInfo.picture);
        
        // Poll for the token
        await pollForToken(state);
        
        setStatus('success');
        
        // Redirect back to the main page or application dashboard
        // setTimeout(() => {
        //   navigate('/');
        // }, 2000);
        
      } catch (err) {
        console.error('Error in OAuth callback:', err);
        setError('An error occurred during authentication');
        setStatus('error');
      }
    };
    
    handleCallback();
  }, [location, navigate]);
  
  const exchangeCodeForTokens = async (code: string): Promise<GoogleTokenResponse | null> => {
    try {
      const response = await fetch('https://oauth2.googleapis.com/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          code,
          client_id: GOOGLE_CLIENT_ID,
          client_secret: GOOGLE_CLIENT_SECRET,
          redirect_uri: GOOGLE_REDIRECT_URI,
          grant_type: 'authorization_code',
        }),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to exchange code: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error exchanging code for tokens:', error);
      return null;
    }
  };
  
  const getUserInfo = async (accessToken: string): Promise<GoogleUserInfo | null> => {
    try {
      const response = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch user info: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching user info:', error);
      return null;
    }
  };
  
  const pollForToken = async (requestId: string): Promise<void> => {
    try {
      // Poll for the token a few times
      for (let i = 0; i < 5; i++) {
        const result = await api.checkLoginStatus(requestId);
        
        if (result.status === 'completed' && result.token) {
          // Store the token in localStorage
          localStorage.setItem('authToken', result.token);
          return;
        }
        
        // Wait for 1 second before trying again
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      
      throw new Error('Timed out waiting for token');
    } catch (error) {
      console.error('Error polling for token:', error);
      throw error;
    }
  };
  
  return (
    <div className="callback-page">
      <div className="container">
        <div className="logo-container">
          <img src="/logo.png" alt="Logo" />
        </div>
        
        <h2>Authentication {status === 'success' ? 'Complete' : 'in Progress'}</h2>
        
        {status === 'processing' && (
          <div className="loading">
            <p>Processing your login...</p>
            <div className="spinner"></div>
          </div>
        )}
        
        {status === 'success' && (
          <div className="success">
            <p>You have been successfully authenticated!</p>
            <p>You can now close this window and return to the application.</p>
          </div>
        )}
        
        {status === 'error' && (
          <div className="error">
            <p>Authentication Error: {error}</p>
            <button 
              className="retry-button"
              onClick={() => navigate('/')}>
              Return to Home
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CallbackPage; 